import { Link } from 'react-router-dom';

const LAST_UPDATED = 'August 2026';
const CONTACT_EMAIL = 'noreply@qmsofts.com';

/**
 * Public, unauthenticated — required by Google Play (and Apple) to be a
 * reachable URL, linked from the store listing. Written against what this
 * app actually does (checked against the real codebase, not a generic
 * template): who staff accounts belong to, what patient online-ordering
 * collects, which third parties actually receive data (Cashfree for
 * payment, Anthropic for prescription OCR), and which device permissions
 * exist and why. This is a good-faith accurate draft, not a substitute for
 * legal review before publishing — flagged again at the bottom.
 */
export function PrivacyPolicyPage() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-12 text-sm leading-relaxed text-foreground">
      <h1 className="font-display text-3xl">Privacy Policy</h1>
      <p className="mt-1 text-xs text-muted-foreground">Last updated: {LAST_UPDATED}</p>

      <p className="mt-6">
        This policy covers the Farmacia app and the Dawa Inventory web application (together, "the
        Service"), operated by QMSofts. It explains what information is collected, how it's used, and who
        it's shared with.
      </p>

      <Section title="Who this applies to">
        <p>The Service is used by two different kinds of people, and what's collected differs between them:</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            <strong>Pharmacy staff</strong> — people who sign up to run their pharmacy's billing, inventory,
            and sales through the Service.
          </li>
          <li>
            <strong>Patients</strong> — people who place an order through a specific pharmacy's own online
            storefront (a link that pharmacy shares, not a general marketplace app).
          </li>
        </ul>
      </Section>

      <Section title="Information we collect">
        <h3 className="mt-3 font-semibold">From pharmacy staff</h3>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>Name, email address, and password (stored as a one-way hash, never in plain text)</li>
          <li>Pharmacy details: name, address, phone number, GSTIN, and drug licence number</li>
          <li>
            Records staff enter while using the Service: medicine inventory, sales and billing records,
            supplier details, and any customer details entered at the counter (name, phone, purchase
            history)
          </li>
        </ul>

        <h3 className="mt-3 font-semibold">From patients ordering online</h3>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>Phone number, verified by a one-time code sent by SMS</li>
          <li>Name and delivery address, if provided</li>
          <li>Order history with that specific pharmacy</li>
        </ul>

        <h3 className="mt-3 font-semibold">Collected automatically</h3>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>Basic crash and error reports, to fix problems in the app</li>
          <li>Standard server access logs (IP address, request timing) retained briefly for security</li>
        </ul>
      </Section>

      <Section title="Device permissions">
        <p>The mobile app requests the following, each used only for the feature it's tied to:</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            <strong>Camera</strong> — scanning a medicine's barcode, and scanning a prescription photo for
            text extraction (see below).
          </li>
          <li>
            <strong>Microphone</strong> — optional voice search when looking up a medicine. Audio is
            processed for that search and not stored.
          </li>
          <li>
            <strong>Fingerprint / Face ID</strong> — used only to lock the app on your own device, if you
            turn that on in Settings. This never leaves your device and nothing about your fingerprint or
            face is sent to us or stored on our servers — the phone's own operating system handles the
            match.
          </li>
        </ul>
      </Section>

      <Section title="How information is used">
        <ul className="list-disc space-y-1 pl-5">
          <li>To provide the billing, inventory, and ordering features themselves</li>
          <li>To verify identity at login (staff password, or a patient's one-time SMS code)</li>
          <li>To process online orders and payment</li>
          <li>To generate a pharmacy's own reports (sales, GST, stock) for its own use</li>
          <li>To respond to support requests</li>
        </ul>
        <p className="mt-2">We do not sell personal information, and we do not use it for advertising.</p>
      </Section>

      <Section title="Who we share information with">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Cashfree Payments</strong> — processes online order payments. Card and payment details
            are handled directly by Cashfree; the Service never sees or stores full card numbers.
          </li>
          <li>
            <strong>Anthropic</strong> — a prescription photo you choose to scan is sent to Anthropic's
            Claude API to extract the medicine names as text. The image is used only for that request.
          </li>
          <li>
            <strong>Our hosting providers</strong> — infrastructure used to run the Service (databases,
            servers, backups).
          </li>
        </ul>
        <p className="mt-2">
          A pharmacy's staff and patient data is only ever visible to that pharmacy's own account — never to
          other pharmacies using the Service.
        </p>
      </Section>

      <Section title="Data retention">
        <p>
          Sales, billing, and inventory records are retained for as long as required by applicable tax and
          business record-keeping law, even after an individual staff account is deleted (see below) — the
          same way a paper ledger would outlive any one employee. Account credentials and other personal
          details are deleted when you request account deletion.
        </p>
      </Section>

      <Section title="Your choices">
        <ul className="list-disc space-y-1 pl-5">
          <li>Staff can update their own profile details from within the app.</li>
          <li>
            Staff can request deletion of their own account and personal details at any time — from
            Settings inside the app, or via the{' '}
            <Link to="/account-deletion" className="text-primary underline">
              account deletion page
            </Link>
            .
          </li>
          <li>Patients can stop using a pharmacy's online ordering at any time; no account is required to browse.</li>
        </ul>
      </Section>

      <Section title="Children">
        <p>The Service is intended for use by pharmacy staff (adults) and adult patients placing their own orders. It is not directed at children.</p>
      </Section>

      <Section title="Changes to this policy">
        <p>If this policy changes materially, the "Last updated" date above will change, and significant changes will be noted in the app.</p>
      </Section>

      <Section title="Contact us">
        <p>
          Questions about this policy, or about your data, can be sent to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <p className="mt-10 border-t border-border/60 pt-4 text-xs text-muted-foreground">
        This document describes the Service's actual data practices as of the date above. It is provided as
        a good-faith description, not legal advice — if your pharmacy operates under additional regulatory
        requirements (health-data rules, local data-protection law), have this reviewed by counsel before
        relying on it.
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="font-display text-lg">{title}</h2>
      <div className="mt-1.5">{children}</div>
    </section>
  );
}